import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircleQuestion } from 'lucide-react';
import { splitCommentParts } from '@shared/comment-short';
import { SUGGESTION_CHAT_MESSAGE_MAX, type SuggestionChat as SuggestionChatData } from '@shared/suggestion-chat';
import { AppAlert } from '@/components/AppAlert';
import { AgentMessageText } from '@/components/factory/AgentMessageText';
import { GrowingTextarea } from '@/components/GrowingTextarea';
import { InfoTooltip } from '@/components/InfoTooltip';
import { LoadError } from '@/components/LoadError';
import { UserMessageBubble } from '@/components/factory/ChatMessageParts';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

const STARTERS = ['Is it worth doing now?', 'How big is it really?', 'What could go wrong?'];

/**
 * SF-T-304: a suggestion's live chat with an agent, as the guard reviews' Ask the AI (RuleAdvice): Ask opens it,
 * the first question starts the chat in the task's worktree, answers show here as they come, follow-ups go to the
 * same chat, and anyone on the task sees it until the suggestion is decided. Without `onAsk` (the suggestion is
 * decided, or the person can't comment) it only shows the conversation, folded behind Show the chat.
 */
export function SuggestionChat({
  title,
  chat,
  onAsk,
  canOpen = false,
  loadError,
  onRetry,
  onStop,
  startOpen = false,
}: {
  /** The suggestion's title, for the box's label. */
  title: string;
  /** Its chat so far; null before anyone asked. */
  chat: SuggestionChatData | null;
  /** Sends a question (starting the chat with the first); resolves an error to show, or null when it went. */
  onAsk?: (text: string) => Promise<string | null>;
  /** Open the chat links to the chat page: the caller's own chat, and they're an admin. */
  canOpen?: boolean;
  /** The chats couldn't be read. */
  loadError?: string | null;
  /** Reads them again (LoadError's Retry). */
  onRetry?: () => void;
  /** Stops the chat (its turn, and anything it waits on); resolves an error to show, or null. Shown while it works or waits. */
  onStop?: () => Promise<string | null>;
  /** Starts open (a preview). */
  startOpen?: boolean;
}) {
  const [open, setOpen] = useState(startOpen);
  const [question, setQuestion] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const messages = chat?.messages ?? [];
  const busy = sending || chat?.status === 'working' || messages.some((m) => m.status === 'queued');
  const closed = chat?.status === 'closed';

  const [stopping, setStopping] = useState(false);
  async function stop() {
    if (!onStop || stopping) return;
    setStopping(true);
    setError(null);
    const failed = await onStop();
    setStopping(false);
    if (failed) setError(failed);
  }

  async function ask(text: string) {
    const q = text.trim();
    if (!q || sending || !onAsk) return;
    setSending(true);
    setError(null);
    const failed = await onAsk(q);
    setSending(false);
    if (failed) setError(failed);
    else setQuestion('');
  }

  if (!open) {
    if (!onAsk && !messages.length) return null;
    const label = messages.length ? `${onAsk ? 'Carry on the chat' : 'Show the chat'} (${messages.length} ${messages.length === 1 ? 'message' : 'messages'})` : 'Ask the AI about it';
    return (
      <span className="flex flex-wrap items-center gap-2 text-xs">
        <Button variant="link" size="xs" className="h-auto gap-1 p-0 text-xs" onClick={() => setOpen(true)} aria-expanded={false}>
          <MessageCircleQuestion className="size-3.5" aria-hidden />
          {label}
        </Button>
        {busy ? (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Spinner className="size-3" /> Answering…
          </span>
        ) : null}
      </span>
    );
  }

  return (
    <section aria-label={`Chat about “${title}”`} className="space-y-3 rounded-lg border bg-card p-3">
      <header className="flex flex-wrap items-center gap-2 text-xs font-medium">
        <MessageCircleQuestion className="size-4 text-muted-foreground" aria-hidden />
        Ask the AI about this suggestion
        <InfoTooltip label="Ask the AI">
          Starts a chat with an agent in the task’s worktree, so it reads what the AI builder built and the suggestion in full. Everyone on the task sees it here, and deciding Yes please, New branch
          or Backlog passes what was said on to whoever builds it. A chat started by someone who isn’t an admin runs read-only: it reads the code and changes nothing.
        </InfoTooltip>
        {chat?.started_by ? <span className="font-normal text-muted-foreground">Started by {chat.started_by}</span> : null}
        <span className="ml-auto flex items-center gap-3 font-normal">
          {chat && canOpen ? (
            <Link to={`/chat/${chat.session_id}`} className="text-primary hover:underline">
              Open the chat
            </Link>
          ) : null}
          <Button variant="link" size="xs" className="h-auto p-0 text-xs text-muted-foreground" onClick={() => setOpen(false)} aria-expanded>
            Hide
          </Button>
        </span>
      </header>

      {messages.length ? (
        <ol className="space-y-3">
          {messages.map((m) =>
            m.role === 'user' ? (
              <li key={m.id} className="flex flex-col items-end gap-0.5">
                <UserMessageBubble text={m.body} className={cn(m.status === 'failed' && 'opacity-60')}>
                  <p className="whitespace-pre-wrap break-words text-sm">{m.body}</p>
                </UserMessageBubble>
                <p className="text-3xs text-muted-foreground">
                  {m.user_name ?? 'Someone'}
                  {m.status === 'queued' ? ' · waiting for the answer before it' : m.status === 'failed' ? ' · not sent' : ''}
                </p>
              </li>
            ) : m.role === 'note' ? (
              <li key={m.id}>
                <AppAlert tone={m.level === 'warn' ? 'warning' : 'critical'} compact>
                  {m.body}
                </AppAlert>
              </li>
            ) : (
              <li key={m.id}>
                <AgentAnswer text={m.body} />
              </li>
            )
          )}
        </ol>
      ) : onAsk ? (
        <div className="flex flex-wrap gap-2">
          {STARTERS.map((s) => (
            <Button key={s} size="xs" variant="outline" onClick={() => void ask(s)} disabled={sending}>
              {s}
            </Button>
          ))}
        </div>
      ) : null}

      {busy || (chat?.waiting && !closed) ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {busy ? (
            <span className="flex items-center gap-2">
              <Spinner className="size-3.5" /> {chat ? 'The agent is reading the task and thinking…' : 'Starting the chat…'}
            </span>
          ) : null}
          {chat && onStop ? (
            <Button size="xs" variant="outline" className="ml-auto" loading={stopping} onClick={() => void stop()} data-hint="Stop the agent and close this chat, freeing its machine; a new question starts a fresh chat with this one carried on">
              Stop the chat
            </Button>
          ) : null}
        </div>
      ) : null}
      {chat?.waiting && !closed ? (
        <AppAlert tone="warning" compact>
          The agent is waiting on an approval or its own question, which only {chat.mine ? 'you' : (chat.started_by ?? 'whoever started it')} can answer, on the chat page.
          {canOpen ? (
            <>
              {' '}
              <Link to={`/chat/${chat.session_id}`} className="font-medium underline">
                Open the chat
              </Link>
            </>
          ) : onStop ? (
            ' Stop the chat to free its machine, then ask again.'
          ) : null}
        </AppAlert>
      ) : null}
      {loadError ? <LoadError compact title="Couldn’t read the chat" error={loadError} onRetry={onRetry} /> : null}
      {error ? <AppAlert tone="critical">{error}</AppAlert> : null}

      {onAsk ? (
        <form
          className="flex flex-col gap-2 sm:flex-row sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            void ask(question);
          }}
        >
          <GrowingTextarea
            aria-label={`Your question about “${title}”`}
            value={question}
            disabled={sending}
            maxLength={SUGGESTION_CHAT_MESSAGE_MAX}
            placeholder={closed ? 'This chat was closed: a question starts a new one…' : messages.length ? 'Ask a follow-up…' : 'e.g. Does this need a migration, or is it front end only?'}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void ask(question);
              }
            }}
            className="flex-1"
          />
          <Button type="submit" size="sm" loading={sending} disabled={!question.trim()}>
            Ask
          </Button>
        </form>
      ) : null}
    </section>
  );
}

/**
 * An agent's answer: its prose. The chat prompt has the agent propose task changes and questions as blocks, which
 * only the chat page applies; this brief asks it not to, and any it sends anyway are left out here with a line.
 */
function AgentAnswer({ text }: { text: string }) {
  const { prose, factoryBlocks } = splitCommentParts(text);
  return (
    <div className="space-y-1">
      {prose ? <AgentMessageText text={prose} /> : null}
      {factoryBlocks ? <p className="text-2xs text-muted-foreground">It also proposed changes or questions, which only the chat page can apply; ask here in words instead.</p> : null}
    </div>
  );
}
